import { Component, OnDestroy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import {
  EndClientReportDeployment,
  EndClientReportDeploymentStatus,
  EndClientReportDomain,
  EndClientReportDomainStatus,
} from '../../models/end-client-reporting-package';
import { EndClientReportService } from './end-client-report.service';

/**
 * Publishing a deck as a read-only share link (NK-399).
 *
 * The thing to understand before changing anything here: **what a client sees is frozen at deploy.**
 * Editing the deck or refreshing its data changes nothing they see until someone re-deploys. That is
 * the feature — numbers already shown to a client don't move under them — and it is also the first
 * thing that will confuse whoever edits a published deck, which is why the dialog says it rather
 * than leaving it to be discovered.
 *
 * One link per report, so re-keying revokes every recipient at once. Deliberately blunt: there is no
 * per-recipient link to revoke individually, and the dialog says so before doing it.
 */
@Component({
  selector: 'app-share-report-modal',
  standalone: false,
  // The report's popup look -- see .ecr-modal in themes/end-client-reporting.scss.
  host: { class: 'ecr-modal' },
  template: `
    <div class="modal-header">
      <h5 class="modal-title nav-font16 font-weight-bold">Share report</h5>
      <button type="button" class="close" aria-label="Close" (click)="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="modal-body">
      @if (notice) {
        <p class="share-notice nav-font12 px-3 py-2 mb-3" role="status">
          <i class="fas fa-check mr-1" aria-hidden="true"></i>{{ notice }}
        </p>
      }

      @if (loading) {
        <p class="grey5 nav-font14 mb-0">Loading…</p>
      } @else if (loadFailed) {
        <p class="grey4 nav-font12 mb-0">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>Couldn't load the
          sharing details for this report.
        </p>
      } @else if (republishing) {
        <!-- Never published, or published and then revoked. Both are about to create a link, so both
             get the same choices: which domain it lives on, and when it stops working. -->
        @if (deployment) {
          <p class="grey5 nav-font14">
            This link was revoked. Publishing again creates a <strong>new link</strong> — the
            withdrawn one stays dead, so anyone who kept it still can't get in.
          </p>
        } @else {
          <p class="grey5 nav-font14">
            Creates a link that opens a read-only copy of this report — no Navigator account needed.
            What it shows is frozen when you publish, so editing the report afterwards won't change
            what they see until you re-publish.
          </p>
        }

        @if (!liveDomains.length) {
          <p class="grey4 nav-font12 mb-0">
            There are no domains set up to publish on yet. An admin adds them under the Domains tab.
          </p>
        } @else {
          <div class="form-group">
            <label class="grey4 nav-font12 mb-1" for="share-domain">Publish on</label>
            <select id="share-domain" class="form-control form-control-sm nav-font14"
                    [(ngModel)]="domainId">
              @for (domain of liveDomains; track domain.id) {
                <option [value]="domain.id">{{ domain.host }}</option>
              }
            </select>
          </div>

          <div class="form-group expiry-field mb-0">
            <label class="grey4 nav-font12" for="share-expiry">Stops working on (optional)</label>
            <small class="grey4 nav-font10">
              Link expires at Midnight UTC on the specified date. Leave empty and it works until revoked.
            </small>
            <!-- Typing blocked here too, for the same reason. Nothing to save yet: this one rides
                 along with Publish. -->
            <input id="share-expiry" type="date" class="form-control form-control-sm nav-font14 expiry-input"
                   [min]="earliestExpiry" [(ngModel)]="expiresAt"
                   (keydown)="$event.preventDefault()">
          </div>
        }
      } @else {
        <!-- Published, in some state. -->
        @if (building) {
          <p class="grey5 nav-font14">
            <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Preparing the report to
            share. This takes a moment, and the link below works as soon as it's done.
          </p>
        } @else if (deployment.status === Status.Failed) {
          <div class="share-error p-3 mb-3">
            <p class="nav-font12 font-weight-bold mb-1">Publishing failed.</p>
            <p class="grey5 nav-font12 mb-0">{{ deployment.lastError }}</p>
          </div>
        } @else if (deployment.status === Status.Revoked) {
          <p class="grey5 nav-font14">
            This link has been revoked. Anyone opening it is told it was withdrawn. Publish again to
            create a new one.
          </p>
        } @else if (deployment.expired) {
          <p class="grey5 nav-font14">
            This link expired on {{ deployment.expiresAt | date:'d MMM yyyy' }}. Clear or change the
            date below to bring it back.
          </p>
        }

        @if (deployment.status !== Status.Revoked) {
          <div class="form-group">
            <label class="grey4 nav-font12 mb-1" for="share-link">Link</label>
            <div class="d-flex">
              <input id="share-link" type="text" class="form-control form-control-sm nav-font12"
                     readonly [value]="deployment.shareUrl">
              <button type="button" class="btn btn-sm btn-outline-secondary ml-2 flex-shrink-0"
                      (click)="copy()">
                <i class="fas mr-1" [class.fa-copy]="!copied" [class.fa-check]="copied" aria-hidden="true"></i>
                {{ copied ? 'Copied' : 'Copy' }}
              </button>
            </div>
          </div>

          <div class="form-group expiry-field">
            <label class="grey4 nav-font12" for="share-expiry-set">Stops working on</label>
            <small class="grey4 nav-font10">Link expires at Midnight UTC on the specified date.</small>
            <div class="d-flex align-items-center">
              <!-- Saved on pick rather than behind a Save button: there is one field and one decision,
                   and a button that must be pressed afterwards is a step to forget. Typing is blocked
                   so the picker is the only way in — a half-typed date reads as empty, which would be
                   indistinguishable from clearing it. -->
              <input id="share-expiry-set" type="date" class="form-control form-control-sm nav-font14 expiry-input"
                     [min]="earliestExpiry" [disabled]="working" [(ngModel)]="expiresAt"
                     (keydown)="$event.preventDefault()"
                     (change)="expiryPicked()">
              @if (expiresAt) {
                <button type="button" class="btn btn-sm btn-link grey5 nav-font12 ml-2"
                        [disabled]="working" (click)="clearExpiry()">Clear</button>
              }
            </div>
          </div>

          @if (deployment.deployedAt) {
            <p class="grey4 nav-font12 mb-1">
              Showing the report as it was on {{ deployment.deployedAt | date:'d MMM yyyy, HH:mm' }}.
              @if (!changedSincePublish) {
                <br/>
                Nothing has changed since.
              }
            </p>
          }

          @if (changedSincePublish) {
            <div class="share-changed p-3 mt-2">
              <p class="nav-font12 font-weight-bold mb-1">{{ changedSincePublish }}</p>
              <p class="grey5 nav-font12 mb-0">
                The client still sees the version above until you re-publish.
              </p>
            </div>
          }

        }
      }

      <!-- Outside both branches: before publishing it says what won't be included, afterwards what
           isn't there. The client's copy leaves those blocks out rather than showing them empty, so
           this is the only place anyone finds out. -->
      @if (!loading && !loadFailed && unresolved.length) {
        <!-- Open when it bears on what you're about to do -- nothing published yet, or something has
             changed since -- and folded away once it is just a standing fact about a deck someone
             came back to for the link. -->
        <div class="share-missing p-3 mt-2">
          <button type="button" class="btn btn-link btn-sm nav-font12 p-0 text-left font-weight-bold"
                  [attr.aria-expanded]="unresolvedOpen"
                  aria-controls="shareUnresolvedList"
                  (click)="toggleUnresolved()">
            {{ unresolved.length }}
            {{ unresolved.length === 1 ? "question isn't" : "questions aren't" }} in this data, and
            won't appear in the shared copy
            <i class="fas ml-1" [class.fa-chevron-down]="!unresolvedOpen"
               [class.fa-chevron-up]="unresolvedOpen" aria-hidden="true"></i>
          </button>

          @if (unresolvedOpen) {
            <div id="shareUnresolvedList" class="mt-2">
              <p class="grey5 nav-font12 mb-1">
                The rest of the report is unaffected. Point the report at data that has
                {{ unresolved.length === 1 ? 'it' : 'them' }} and publish again to include
                {{ unresolved.length === 1 ? 'it' : 'them' }}.
              </p>
              <span class="grey4 nav-font12">{{ unresolved.join(', ') }}</span>
            </div>
          }
        </div>
      }
    </div>

    <div class="modal-footer">
      @if (deployment && !loading) {
        <!-- Destructive actions on the left, away from the primary button, and each states its blast
             radius rather than asking "are you sure?". -->
        <div class="mr-auto d-flex">
          @if (deployment.status !== Status.Revoked) {
            <button type="button" class="btn btn-sm btn-link grey5 nav-font12"
                    [disabled]="working" (click)="rekey()">
              New link
            </button>
            <button type="button" class="btn btn-sm btn-link nav-font12 revoke"
                    [disabled]="working" (click)="revoke()">
              Revoke
            </button>
          }
        </div>
      }

      <button type="button" class="btn btn-sm btn-link grey5" (click)="close()">Close</button>

      @if (!loading && !loadFailed) {
        @if (republishing) {
          <!-- Goes through publish() rather than redeploy: this is the call that carries a domain and
               an expiry, so a revoked link can come back somewhere else. -->
          <button type="button" class="btn btn-sm btn-primary"
                  [disabled]="working || !domainId || !liveDomains.length" (click)="publish()">
            @if (working) {
              <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
            }
            {{ deployment ? 'Publish again' : 'Publish' }}
          </button>
        } @else if (canRepublish) {
          <button type="button" class="btn btn-sm btn-primary"
                  [disabled]="working || building" (click)="republish()">
            @if (working || building) {
              <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
            }
            {{ deployment.status === Status.Revoked ? 'Publish again' : 'Re-publish' }}
          </button>
        }
      }
    </div>
  `,
  styles: [`
    .share-error {
      background-color: var(--nav-error-light, #fdecea);
      border-radius: 4px;
    }

    .share-error p:first-child {
      color: var(--nav-error, #b3261e);
    }

    .revoke {
      color: var(--nav-error, #b3261e);
    }

    /* A caution, not a failure: the deck published, it is just carrying less than the design asks
       for. Coloured to read as information rather than an error. */
    .share-missing {
      background-color: var(--nav-grey1, #f7f7f7);
      border-left: 3px solid var(--nav-warning, #d78b00);
      border-radius: 4px;
    }

    /* Information, not a problem: the published copy is fine, it just isn't the latest. */
    .share-changed {
      background-color: var(--nav-grey1, #f7f7f7);
      border-left: 3px solid var(--nav-primary, #2f6fed);
      border-radius: 4px;
    }

    .share-notice {
      background-color: var(--nav-success-light, #e6f4ea);
      color: var(--nav-success, #1e7e34);
      border-radius: 4px;
    }

    /* The label and its note are one thought, so they sit tight together and the gap goes between
       that pair and the field. Done with margins rather than a zero line-height on the label, which
       collapses the text's own box and leaves the spacing at the mercy of the font. */
    .expiry-field label {
      display: block;
      margin-bottom: 0;
    }

    .expiry-field small {
      display: block;
      margin-bottom: 0.4rem;
    }

    /* Sized to the date it holds rather than stretching across the dialog. */
    .expiry-input {
      max-width: 180px;
    }
  `]
})
export class ShareReportModalComponent implements OnDestroy {
  readonly Status = EndClientReportDeploymentStatus;

  reportId = '';

  /**
   * The design's variables the deck couldn't resolve against its data, from `unresolvedVariables()`
   * — the same function behind the in-deck banner. Passed in rather than fetched: the deck already
   * has the design and the datamap loaded, so asking the server would be re-deriving what is already
   * in hand.
   *
   * Sent with a publish, where the server checks it against its own answer and refuses if they
   * differ. That is the only runtime check on the TypeScript and C# agreeing about a survey.
   */
  unresolved: string[] = [];

  /**
   * The deck's current design version and the age of its data, for telling whether what a client
   * sees has fallen behind. Both come off the report the deck already loaded — the published copy's
   * own versions are on the deployment, so the comparison needs no extra fetch.
   */
  designVersion = 0;
  dataFetchedAt: string | null = null;

  /** Confirmation of something that finished instantly, cleared after a few seconds. */
  notice: string | null = null;

  /** Null until someone opens or closes the missing-questions note themselves; then it is their call. */
  private unresolvedToggled: boolean | null = null;

  onChanged?: (deployment: EndClientReportDeployment | null) => void;

  loading = true;
  loadFailed = false;
  working = false;
  copied = false;

  domains: EndClientReportDomain[] = [];
  deployment: EndClientReportDeployment | null = null;

  domainId = '';
  /** yyyy-MM-dd, which is what <input type="date"> reads and writes. */
  expiresAt = '';

  private destroy$ = new Subject<void>();
  private polling$ = new Subject<void>();

  constructor(
    public modalRef: BsModalRef,
    private reportService: EndClientReportService,
  ) { }

  ngOnDestroy() {
    this.polling$.next();
    this.polling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Whether the missing-questions note is showing its detail.
   *
   * Open by default while it could change what you do next: nothing published yet, something changed
   * since, or the link is revoked — all three are a moment before publishing, which is exactly when
   * knowing what won't be included matters. Once a deck is published and current, the same fact is
   * background: worth stating, not worth unfolding every time someone opens the dialog to copy the
   * link. A toggle by hand wins from then on.
   */
  get unresolvedOpen(): boolean {
    if (this.unresolvedToggled !== null) return this.unresolvedToggled;
    if (this.deployment?.status === EndClientReportDeploymentStatus.Revoked) return true;

    return !this.deployment?.deployedAt || !!this.changedSincePublish;
  }

  toggleUnresolved() {
    this.unresolvedToggled = !this.unresolvedOpen;
  }

  /**
   * The earliest date the picker offers: **tomorrow**, not today.
   *
   * Expiry lands at midnight UTC as the chosen date begins, so today has already passed — choosing it
   * would publish a link that is expired on arrival. Computed in UTC to match how the value is read
   * back, so it doesn't shift for anyone east or west of us.
   */
  get earliestExpiry(): string {
    const tomorrow = new Date();
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    return tomorrow.toISOString().slice(0, 10);
  }

  /** Only a Live domain can be published on: a Pending one has no certificate, so the link wouldn't resolve. */
  get liveDomains(): EndClientReportDomain[] {
    return this.domains.filter(domain => domain.status === EndClientReportDomainStatus.Live);
  }

  get building(): boolean {
    return this.deployment?.status === EndClientReportDeploymentStatus.Building;
  }

  /**
   * What has moved on since the published copy was built, or null when nothing has.
   *
   * Two cheap comparisons against what the deploy recorded — no extra fetch, and no reading the
   * bundle back. **Re-publish stays available either way**: the one time someone most wants it is
   * after a failed build or when they simply want to be sure, and a button that vanishes with no
   * explanation is worse than a note that is occasionally over-eager.
   *
   * Over-eager it can be: a data refresh that returns identical rows still moves `dataFetchedAt`, so
   * this will sometimes say the data changed when the numbers didn't. Saying so costs a needless
   * re-publish; the alternative -- reading the bundle to compare content -- costs an S3 round trip
   * every time this dialog opens.
   */
  /**
   * Whether re-publishing would do anything, and therefore whether the button is offered at all.
   *
   * Hidden when the published copy is already current: there is nothing to update, and a live button
   * that rebuilds an identical bundle only invites someone to wonder whether it worked.
   *
   * Still offered in the three cases where it does something: the deck or its data has moved on, the
   * last build failed, or the link was revoked and wants publishing again. `building` keeps its own
   * spinner rather than disappearing mid-run.
   */
  /**
   * Whether the dialog is asking where to publish, rather than showing a link it already has.
   *
   * True for a deck that has never been published and for one whose link was revoked: both are about
   * to create a link, so both get the domain and expiry choices. Publishing again after a revoke
   * issues a new key server-side, so it genuinely is a new link rather than the old one restored.
   */
  get republishing(): boolean {
    return !this.deployment || this.deployment.status === EndClientReportDeploymentStatus.Revoked;
  }

  get canRepublish(): boolean {
    // Revoked isn't here: that state shows the publish form instead, which offers a domain too.
    if (!this.deployment) return false;
    if (this.building) return true;
    if (this.deployment.status === EndClientReportDeploymentStatus.Failed) return true;
    if (!this.deployment.deployedAt) return true;

    return !!this.changedSincePublish;
  }

  get changedSincePublish(): string | null {
    if (!this.deployment?.deployedAt) return null;
    if (this.deployment.status === EndClientReportDeploymentStatus.Revoked) return null;

    const designMoved = !!this.deployment.deployedDesignVersion
      && this.designVersion > this.deployment.deployedDesignVersion;
    const dataMoved = !!this.dataFetchedAt
      && new Date(this.dataFetchedAt).getTime() > new Date(this.deployment.deployedAt).getTime();

    if (designMoved && dataMoved) return 'The report and its data have both changed since you published.';
    if (designMoved) return 'The report has been edited since you published.';
    if (dataMoved) return 'The data has been refreshed since you published.';
    return null;
  }

  load() {
    this.loading = true;
    this.loadFailed = false;

    this.reportService.getDomainsForSharing()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: domains => {
          this.domains = domains ?? [];
          this.domainId = this.liveDomains[0]?.id ?? '';
          this.loadDeployment();
        },
        error: () => {
          this.loading = false;
          this.loadFailed = true;
        },
      });
  }

  publish() {
    if (this.working || !this.domainId) return;

    this.working = true;
    this.reportService.deployReport(this.reportId, this.domainId, this.toIso(this.expiresAt), this.unresolved)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deployment => this.applied(deployment),
        error: () => { this.working = false; this.loadFailed = true; },
      });
  }

  republish() {
    if (this.working || !this.deployment) return;

    this.working = true;
    this.reportService.redeployReport(this.reportId, this.unresolved)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deployment => this.applied(deployment),
        error: () => { this.working = false; },
      });
  }

  /**
   * A date came out of the picker. Saved straight away when it is one we'd accept.
   *
   * Silent about the two cases it skips, both of which the picker shouldn't produce: an empty value
   * (the browser's own clear — removing an expiry is what the Clear button is for, and doing it
   * implicitly here would make an accidental keystroke look like a deliberate act) and a date before
   * tomorrow, which `min` greys out but a keyboard could still reach.
   */
  expiryPicked() {
    if (!this.expiresAt) return;
    if (this.expiresAt < this.earliestExpiry) return;

    this.saveExpiry();
  }

  /** Removes the expiry: the link then works until someone revokes it. */
  clearExpiry() {
    if (this.working || !this.deployment) return;

    this.expiresAt = '';
    this.saveExpiry();
  }

  saveExpiry() {
    if (this.working || !this.deployment) return;

    this.working = true;
    const clearing = !this.expiresAt;
    this.reportService.setDeploymentExpiry(this.reportId, this.toIso(this.expiresAt))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deployment => {
          this.applied(deployment);
          this.say(clearing ? 'Expiry removed. The link works until it is revoked.' : 'Expiry saved.');
        },
        error: () => { this.working = false; },
      });
  }

  /**
   * Confirmed in the browser rather than with a second dialog: it is one sentence, and the sentence
   * is the whole point -- every link already sent stops working, including ones we can't see.
   */
  rekey() {
    if (this.working || !this.deployment) return;
    if (!confirm('Everyone who already has this link will lose access, and you\'ll need to send the new one. Continue?')) return;

    this.working = true;
    this.reportService.rekeyReport(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deployment => {
          this.applied(deployment);
          // It finishes in a blink and the link in the field looks much the same at a glance, so
          // without this it's hard to tell whether the button did anything.
          this.say('New link issued. The previous one no longer works.');
        },
        error: () => { this.working = false; },
      });
  }

  revoke() {
    if (this.working || !this.deployment) return;
    if (!confirm('The link stops working for everyone. Anyone opening it is told it was withdrawn. Continue?')) return;

    this.working = true;
    this.reportService.revokeReport(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deployment => {
          this.applied(deployment);
          this.say('Link revoked. Anyone opening it is told it was withdrawn.');
        },
        error: () => { this.working = false; },
      });
  }

  /** Says something happened, then gets out of the way. */
  private say(message: string) {
    this.notice = message;
    timer(5000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.notice === message) this.notice = null;
    });
  }

  copy() {
    if (!this.deployment?.shareUrl) return;

    navigator.clipboard?.writeText(this.deployment.shareUrl).then(() => {
      this.copied = true;
      timer(2000).pipe(takeUntil(this.destroy$)).subscribe(() => this.copied = false);
    });
  }

  close() {
    this.modalRef.hide();
  }

  private loadDeployment() {
    this.reportService.getDeployment(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deployment => {
          this.deployment = deployment ?? null;
          this.expiresAt = this.toInput(deployment?.expiresAt);
          // A revoked link shows the publish form again, so start it on the domain it was on rather
          // than whichever happens to sort first.
          if (deployment?.domainId && this.liveDomains.some(domain => domain.id === deployment.domainId)) {
            this.domainId = deployment.domainId;
          }
          this.loading = false;
          if (this.building) this.poll();
        },
        error: () => {
          this.loading = false;
          this.loadFailed = true;
        },
      });
  }

  private applied(deployment: EndClientReportDeployment) {
    this.working = false;
    this.deployment = deployment;
    this.expiresAt = this.toInput(deployment?.expiresAt);
    this.onChanged?.(deployment);
    if (this.building) this.poll();
  }

  /**
   * The build runs after the response, as an ingestion does, so the dialog polls until it lands.
   * Restarted rather than stacked: a second re-publish while one is in flight would otherwise leave
   * two timers writing to the same field.
   */
  private poll() {
    this.polling$.next();

    timer(2000, 2000)
      .pipe(
        switchMap(() => this.reportService.getDeployment(this.reportId)),
        takeUntil(this.polling$),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: deployment => {
          this.deployment = deployment ?? this.deployment;
          this.onChanged?.(this.deployment);
          if (!this.building) this.polling$.next();
        },
      });
  }

  /**
   * Midnight UTC on the date chosen — so the link stops working as that day begins, everywhere at
   * once. A local midnight would expire the same link at different moments for the sender and the
   * recipient, which is the kind of thing nobody notices until a client loses access mid-meeting.
   *
   * The dialog says "Midnight UTC" beside the field, because "stops working on the 3rd" and "works
   * through the 3rd" are different promises and the reader can't tell which from a date alone.
   */
  private toIso(value: string): string | null {
    return value ? new Date(value + 'T00:00:00Z').toISOString() : null;
  }

  private toInput(value?: string | null): string {
    return value ? new Date(value).toISOString().slice(0, 10) : '';
  }
}
